import { useEffect } from 'react';

const CSS_VAR = '--vvh';

/**
 * Syncs `window.visualViewport.height` to a CSS custom property `--vvh`.
 *
 * On mobile web, when the virtual keyboard opens, the browser shifts the
 * Visual Viewport upward but does NOT resize the Layout Viewport (CSS `vh`/`dvh`
 * units stay the same). This hook tracks the actual visible height and exposes
 * it as a CSS variable so containers can resize dynamically.
 *
 * Usage:
 * ```tsx
 * useVisualViewportHeight({ enabled: isMobile });
 * // Then in CSS: height: var(--vvh, 100dvh)
 * ```
 */
export function useVisualViewportHeight({ enabled }: { enabled: boolean }) {
  useEffect(() => {
    if (!enabled) return;

    const vv = window.visualViewport;

    function sync() {
      const h = vv?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(CSS_VAR, `${Math.round(h)}px`);
    }

    sync();
    vv?.addEventListener('resize', sync);
    window.addEventListener('resize', sync);

    return () => {
      vv?.removeEventListener('resize', sync);
      window.removeEventListener('resize', sync);
      document.documentElement.style.removeProperty(CSS_VAR);
    };
  }, [enabled]);
}
